import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiExError } from '@shared';

import { FundingPolicyType } from '@shared/appModel';

export type LtcFinancialPlanningSliceState = LtcFinancialPlanningState;

const initialState: LtcFinancialPlanningSliceState = {
  policyPreferences: {
    deathBenefit: null,
    paymentStructure: null,
    benefitStructure: null,
  },
  selectedPolicyTypes: [],
  selectedAccountTypes: [],
  isLoading: true,
};

const ltcFinancialPlanningSlice = createSlice({
  name: 'ltcFinancialPlanning',
  initialState,
  reducers: {
    setPolicyPreference(
      state,
      action: PayloadAction<{ key: PolicyPreferenceKey; value: PolicyPreferenceValue }>,
    ) {
      const { key, value } = action.payload;
      if (key === 'deathBenefit') {
        state.policyPreferences.deathBenefit =
          value as LtcFinancialPlanningDeathBenefitPreference | null;
      } else if (key === 'paymentStructure') {
        state.policyPreferences.paymentStructure =
          value as LtcFinancialPlanningPaymentStructure | null;
      } else if (key === 'benefitStructure') {
        state.policyPreferences.benefitStructure =
          value as LtcFinancialPlanningBenefitStructure | null;
      }
    },
    setPolicyPreferences(state, action: PayloadAction<Partial<PolicyPreferences>>) {
      state.policyPreferences = { ...state.policyPreferences, ...action.payload };
    },
    setSelectedAccountTypes(state, action: PayloadAction<string[]>) {
      state.selectedAccountTypes = action.payload;
    },
    resetLtcFinancialPlanning() {
      return initialState;
    },

    putLtcFinancialPlanningStateByClientIdRequest(
      state,
      action: PayloadAction<{
        clientId: string;
        stateData: Omit<LtcFinancialPlanningState, 'isLoading'>;
      }>,
    ) {
      // Only set loading state - wait for server response to update state
      state.isLoading = true;
    },

    putLtcFinancialPlanningStateByClientIdResponse(
      state,
      action: PayloadAction<{
        id: string;
        clientId: string;
        stateData: Omit<LtcFinancialPlanningState, 'isLoading'>;
        updatedAt: string;
      }>,
    ) {
      // Update state with full response from server
      return {
        ...action.payload.stateData,
        isLoading: false,
      };
    },

    putLtcFinancialPlanningStateByClientIdFailure(state, action: PayloadAction<ApiExError>) {
      // Reset loading state on error, keep previous state values
      state.isLoading = false;
    },

    fetchLtcFinancialPlanningStateByClientIdRequest(
      state,
      action: PayloadAction<{ clientId: string }>,
    ) {
      state.isLoading = true;
    },

    fetchLtcFinancialPlanningStateByClientIdResponse(
      state,
      action: PayloadAction<{
        stateData:
          | (Omit<LtcFinancialPlanningState, 'isLoading'> & {
              preferences?: PolicyPreferences;
            })
          | null;
        id?: string;
        clientId?: string;
        createdAt?: string;
        updatedAt?: string;
      }>,
    ) {
      if (action.payload.stateData) {
        const { preferences, ...stateData } = action.payload.stateData as any;
        const policyPreferences =
          stateData.policyPreferences ?? preferences ?? initialState.policyPreferences;
        return {
          ...stateData,
          policyPreferences,
          isLoading: false,
        };
      }
      return {
        ...initialState,
        isLoading: false,
      };
    },

    fetchLtcFinancialPlanningStateByClientIdFailure(state, action: PayloadAction<ApiExError>) {
      return {
        ...initialState,
        isLoading: false,
      };
    },
  },
});

export const {
  setPolicyPreference,
  setPolicyPreferences,
  setSelectedAccountTypes,
  resetLtcFinancialPlanning,
  putLtcFinancialPlanningStateByClientIdRequest,
  putLtcFinancialPlanningStateByClientIdResponse,
  putLtcFinancialPlanningStateByClientIdFailure,
  fetchLtcFinancialPlanningStateByClientIdRequest,
  fetchLtcFinancialPlanningStateByClientIdResponse,
  fetchLtcFinancialPlanningStateByClientIdFailure,
} = ltcFinancialPlanningSlice.actions;

export const ltcFinancialPlanningReducer = ltcFinancialPlanningSlice.reducer;
